import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, type UseFormReturn } from 'react-hook-form';
import dayjs from 'dayjs';

import StoreCheckoutColorPicker from '../StoreCheckoutColorPicker/StoreCheckoutColorPicker';
import {
  applyDefaultFreshPrintBrandingToForm,
  isDefaultFreshPrintStoreLogo,
  resolveStoreLogoPreviewSrc,
} from '../helpers/storeCheckoutUiVariationBranding.utils';
import { useStoreCheckoutUiVariationOptional } from '../context/StoreCheckoutUiVariationContext';
import {
  STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES,
  STORE_CHECKOUT_DATE_PICKER_POPUP_CLASS,
  STORE_INFO_CHAR_LIMITS,
  STORE_LOGO_UPLOAD,
  STORE_LOGO_UPLOAD_MESSAGES,
  STORE_PRIMARY_COLOR_SOURCE,
  STORES_FORM_MIXPANEL_SECTIONS,
  STORE_LAUNCH_DISABLED_MESSAGES,
  showsFundraiserStoreInfoFieldsInCheckoutVariation,
} from '../constants/storeCheckout.constants';
import { buildStoreCustomizationUpdatePayload } from '../helpers/storeCheckoutCustomizationPayload.utils';
import type { IStoreCustomizationBrandingState } from '../helpers/storeCheckoutCustomizationPayload.utils';
import { resolveStoreCheckoutOrderIdFromUrl } from '../helpers/storeCheckoutStoreDetails.utils';
import { buildStoreOrderUntilUpdatePayload } from '../helpers/storeCheckoutDate.utils';
import { useStoreBranding } from './helpers/useStoreBranding';
import { uploadStoreLogoFileToS3 } from './helpers/storeLogoS3.utils';
import type { IDesignStoreFormShape, IStoreInfoFormShape } from './storeDetails.types';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useGetPublicPreSignedUrlMutation } from '@/api/S3Query';
import { useUpdateStoreMutation } from '@/api/StoreQuery';
import { captureStoresFormSectionCompleted } from '@/mixpanel/MixpanelEvents';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';
import { getUtcDateString } from '@/utilities/CommonUtils';
import DatePickerComponent from '@/components/DatePicker/DatePicker';
import Input from '@/components/Input/Input';
import Textarea from '@/components/Textarea/Textarea';

import './StoreDetails.scss';

interface IStoreDetailsProps {
  storeType?: string;
  userName: string;
  isReadOnly?: boolean;
  isEditStorePage?: boolean;
  designForm: UseFormReturn<IDesignStoreFormShape>;
  savedBranding?: IStoreCustomizationBrandingState | null;
  onBrandingStateChange?: (branding: IStoreCustomizationBrandingState) => void;
}

const StoreDetails: React.FC<IStoreDetailsProps> = ({
  storeType,
  userName,
  isReadOnly = false,
  isEditStorePage = false,
  designForm,
  savedBranding = null,
  onBrandingStateChange,
}) => {
  const [orderUntilDefaultPickerValue, setOrderUntilDefaultPickerValue] = useState(() => dayjs());
  const { control, watch, getValues } = useFormContext<IStoreInfoFormShape>();
  const uiVariationCtx = useStoreCheckoutUiVariationOptional();
  const [updateStore] = useUpdateStoreMutation();
  const [getPublicPreSignedUrl] = useGetPublicPreSignedUrlMutation();
  const { user } = useAppSelector((state: RootState) => state.user);
  const hasTrackedStoreInfoSectionRef = useRef(false);
  const storeIdFromUrl = useMemo(() => resolveStoreCheckoutOrderIdFromUrl(), []);
  const storeName = watch('storeName') ?? '';
  const storeDescription = watch('storeDescription') ?? '';
  const isPermanentStore = storeType === 'permanent-store';

  const maybeTrackStoreInfoSectionCompleted = useCallback(
    (nextStoreName?: string, nextOrderUntil?: string) => {
      if (
        hasTrackedStoreInfoSectionRef.current ||
        isReadOnly ||
        isEditStorePage ||
        isPermanentStore
      ) {
        return;
      }

      const formValues = getValues();
      const trimmedStoreName = (nextStoreName ?? formValues.storeName ?? '').trim();
      const orderUntilValue = (nextOrderUntil ?? formValues.orderUntil ?? '').trim();

      if (!trimmedStoreName || !orderUntilValue) {
        return;
      }

      hasTrackedStoreInfoSectionRef.current = true;
      captureStoresFormSectionCompleted(
        user?.email ?? '',
        STORES_FORM_MIXPANEL_SECTIONS.STORE_INFO,
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Name: trimmedStoreName,
          'Store Deadline': getUtcDateString(orderUntilValue, true),
        }
      );
    },
    [getValues, isEditStorePage, isPermanentStore, isReadOnly, user?.email]
  );

  const debouncedUpdateStoreName = useDebouncedCallback((nextStoreName: string) => {
    if (!storeIdFromUrl || isReadOnly || isEditStorePage) {
      return;
    }

    updateStore({
      order_id: Number(storeIdFromUrl),
      name: nextStoreName,
    })
      .unwrap()
      .catch(() => {
        /* store name save is best-effort when store exists */
      });
  }, 500);

  const handleStoreNameChange = useCallback(
    (nextStoreName: string, onChange: (value: string) => void) => {
      onChange(nextStoreName);
      debouncedUpdateStoreName(nextStoreName);
      maybeTrackStoreInfoSectionCompleted(nextStoreName);
    },
    [debouncedUpdateStoreName, maybeTrackStoreInfoSectionCompleted]
  );

  const debouncedUpdateStoreDescription = useDebouncedCallback((nextDescription: string) => {
    if (!storeIdFromUrl || isReadOnly || isEditStorePage) {
      return;
    }

    updateStore({
      order_id: Number(storeIdFromUrl),
      description: nextDescription,
    })
      .unwrap()
      .catch(() => {
        /* store description save is best-effort when store exists */
      });
  }, 500);

  const handleStoreDescriptionChange = useCallback(
    (nextDescription: string, onChange: (value: string) => void) => {
      onChange(nextDescription);
      debouncedUpdateStoreDescription(nextDescription);
    },
    [debouncedUpdateStoreDescription]
  );

  const debouncedUpdateFundraiserCause = useDebouncedCallback((nextCause: string) => {
    if (!storeIdFromUrl || isReadOnly) {
      return;
    }

    updateStore({
      order_id: Number(storeIdFromUrl),
      fundraiser_cause: nextCause,
    })
      .unwrap()
      .catch(() => {
        /* fundraiser cause save is best-effort when store exists */
      });
  }, 500);

  const handleFundraiserCauseChange = useCallback(
    (nextCause: string, onChange: (value: string) => void) => {
      onChange(nextCause);
      debouncedUpdateFundraiserCause(nextCause);
    },
    [debouncedUpdateFundraiserCause]
  );

  const debouncedUpdateFundraiserGoal = useDebouncedCallback((nextGoal: string) => {
    if (!storeIdFromUrl || isReadOnly) {
      return;
    }

    updateStore({
      order_id: Number(storeIdFromUrl),
      fundraiser_goal: nextGoal,
    })
      .unwrap()
      .catch(() => {
        /* fundraiser goal save is best-effort when store exists */
      });
  }, 500);

  const handleFundraiserGoalChange = useCallback(
    (nextGoal: string, onChange: (value: string) => void) => {
      onChange(nextGoal);
      debouncedUpdateFundraiserGoal(nextGoal);
    },
    [debouncedUpdateFundraiserGoal]
  );

  const handleOrderUntilChange = useCallback(
    (nextOrderUntil: string, onChange: (value: string) => void) => {
      onChange(nextOrderUntil);
      maybeTrackStoreInfoSectionCompleted(undefined, nextOrderUntil);

      if (
        !storeIdFromUrl ||
        isEditStorePage ||
        (isReadOnly && !isEditStorePage) ||
        !nextOrderUntil
      ) {
        return;
      }

      const orderUntilPayload = buildStoreOrderUntilUpdatePayload(
        Number(storeIdFromUrl),
        nextOrderUntil
      );

      if (!orderUntilPayload) {
        return;
      }

      updateStore(orderUntilPayload)
        .unwrap()
        .catch(() => {
          /* order until save is best-effort when store exists */
        });
    },
    [isEditStorePage, isReadOnly, maybeTrackStoreInfoSectionCompleted, storeIdFromUrl, updateStore]
  );

  const handleDisplayMadeWithChange = useCallback(
    (checked: boolean, onChange: (value: boolean) => void) => {
      onChange(checked);

      if (!storeIdFromUrl || isReadOnly || isEditStorePage) {
        return;
      }

      updateStore({
        order_id: Number(storeIdFromUrl),
        display_made_with_love: checked ? 1 : 0,
      })
        .unwrap()
        .catch(() => {
          /* display made with love save is best-effort when store exists */
        });
    },
    [isEditStorePage, isReadOnly, storeIdFromUrl, updateStore]
  );

  const persistStoreCustomization = useCallback(
    (branding: IStoreCustomizationBrandingState) => {
      onBrandingStateChange?.(branding);

      if (!storeIdFromUrl || isReadOnly || isEditStorePage) {
        return;
      }

      updateStore(buildStoreCustomizationUpdatePayload(Number(storeIdFromUrl), branding))
        .unwrap()
        .catch(() => {
          /* store customization save is best-effort when store exists */
        });
    },
    [isEditStorePage, isReadOnly, onBrandingStateChange, storeIdFromUrl, updateStore]
  );

  const uploadLogoToS3 = useCallback(
    async (file: File) =>
      uploadStoreLogoFileToS3(file, (payload) => getPublicPreSignedUrl(payload).unwrap()),
    [getPublicPreSignedUrl]
  );

  const isFundraiser = storeType === 'fundraiser';
  const showFundraiserStoreInfoFields =
    isFundraiser &&
    (!uiVariationCtx ||
      showsFundraiserStoreInfoFieldsInCheckoutVariation(uiVariationCtx.uiVariation));

  useEffect(() => {
    if (storeIdFromUrl || savedBranding) {
      return;
    }

    const logo = (designForm.getValues('storeLogo') ?? '').toString().trim();
    if (!logo || isDefaultFreshPrintStoreLogo(logo)) {
      applyDefaultFreshPrintBrandingToForm(designForm);
    }
  }, [designForm, savedBranding, storeIdFromUrl]);

  const branding = useStoreBranding({
    designForm,
    onCustomizationChange: persistStoreCustomization,
    uploadLogoToS3,
    savedBranding,
  });
  const storeLogoPreviewSrc = resolveStoreLogoPreviewSrc(branding.storeLogo);

  const madeWithLoveLabel = `Display "Designed with ❤️ by ${userName}" on the store link?`;

  const allPresetSwatches = STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES;

  const colorPickerExistingColors = [
    ...allPresetSwatches,
    ...branding.extractedColors,
    ...branding.customColors,
  ];

  return (
    <div className="store-details-form">
      <div className="store-details-form__rows">
        <div className="store-details-form__row">
          <div className="store-details-form__field">
            <div className="store-details-form__label">Store Name</div>
            <Controller
              name="storeName"
              control={control}
              rules={{
                required: 'Store name is required',
                maxLength: {
                  value: STORE_INFO_CHAR_LIMITS.ORDER_NAME,
                  message: `Max ${STORE_INFO_CHAR_LIMITS.ORDER_NAME} characters`,
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <div className="store-details-form__input">
                    <Input
                      {...field}
                      name="store-name"
                      label="Store Name"
                      floatingLabel={false}
                      type="text"
                      disabled={isReadOnly}
                      fieldErrMessg={fieldState.error?.message}
                      onChange={(e) => handleStoreNameChange(e.target.value, field.onChange)}
                    />
                  </div>
                  <span
                    className={`store-details-form__char-count ${
                      storeName.length >= STORE_INFO_CHAR_LIMITS.ORDER_NAME
                        ? 'store-details-form__char-count--at-max'
                        : ''
                    }`}
                    aria-live="polite"
                  >
                    {storeName.length}/{STORE_INFO_CHAR_LIMITS.ORDER_NAME}
                  </span>
                </>
              )}
            />
          </div>

          {!isPermanentStore && (
            <div className="store-details-form__field">
              <div className="store-details-form__label">People Can Order Until</div>
              <Controller
                name="orderUntil"
                control={control}
                rules={{ required: 'Order until date is required' }}
                render={({ field, fieldState: { error } }) => {
                  const selectedOrderUntil =
                    field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null;

                  return (
                    <div className="store-details-form__input">
                      <div
                        className={`store-details-form__date-field${
                          isEditStorePage ? ' store-details-form__date-field--locked' : ''
                        }`}
                      >
                        <DatePickerComponent
                          value={selectedOrderUntil}
                          floatingLabel={false}
                          size="middle"
                          placeholder="e.g. Aug 26, 2025"
                          format="MMM DD, YYYY"
                          defaultPickerValue={orderUntilDefaultPickerValue}
                          onChange={(date) => {
                            const nextOrderUntil = date ? date.format('YYYY-MM-DD') : '';
                            handleOrderUntilChange(nextOrderUntil, field.onChange);
                          }}
                          status={error ? 'error' : ''}
                          fieldErrMessg={error?.message ?? ''}
                          disabledDate={(current) =>
                            current ? current.isBefore(dayjs().startOf('day')) : false
                          }
                          disabled={isReadOnly || isEditStorePage}
                          suffixIcon={null}
                          placement="bottomLeft"
                          getPopupContainer={() => document.body}
                          popupClassName={STORE_CHECKOUT_DATE_PICKER_POPUP_CLASS}
                          onOpenChange={(open) => {
                            if (open && isEditStorePage) {
                              const selected = field.value ? dayjs(field.value) : dayjs();
                              setOrderUntilDefaultPickerValue(
                                selected.isValid() && selected.isBefore(dayjs().startOf('day'))
                                  ? dayjs()
                                  : selected
                              );
                            }

                            if (!open) {
                              field.onBlur();
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>

        <div className="store-details-form__row">
          <div className="store-details-form__field store-details-form__field--full-width">
            <div className="store-details-form__label">Store Description</div>
            <Controller
              name="storeDescription"
              control={control}
              rules={{
                required: STORE_LAUNCH_DISABLED_MESSAGES.STORE_DESCRIPTION_REQUIRED,
                maxLength: {
                  value: STORE_INFO_CHAR_LIMITS.STORE_DESCRIPTION,
                  message: `Max ${STORE_INFO_CHAR_LIMITS.STORE_DESCRIPTION} characters`,
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="store-details-form__description-area">
                    <Textarea
                      {...field}
                      name="store-description"
                      rows={4}
                      placeholder="e.g. Drip for Philo's upcoming event"
                      disabled={isReadOnly}
                      maxLength={STORE_INFO_CHAR_LIMITS.STORE_DESCRIPTION}
                      fieldErrMessg={error?.message ?? ''}
                      onChange={(e) => handleStoreDescriptionChange(e.target.value, field.onChange)}
                    />
                  </div>
                  <span
                    className={`store-details-form__char-count ${
                      storeDescription.length >= STORE_INFO_CHAR_LIMITS.STORE_DESCRIPTION
                        ? 'store-details-form__char-count--at-max'
                        : ''
                    }`}
                    aria-live="polite"
                  >
                    {storeDescription.length}/{STORE_INFO_CHAR_LIMITS.STORE_DESCRIPTION}
                  </span>
                </>
              )}
            />
          </div>
        </div>

        {showFundraiserStoreInfoFields && (
          <div className="store-details-form__row">
            <div className="store-details-form__field">
              <div className="store-details-form__label">
                What&apos;s the name of the Charity/Cause? (Optional)
              </div>
              <Controller
                name="charityName"
                control={control}
                render={({ field }) => (
                  <>
                    <div className="store-details-form__input">
                      <Input
                        {...field}
                        name="charity-name"
                        label="e.g. Feeding America"
                        floatingLabel={false}
                        type="text"
                        disabled={isReadOnly}
                        onChange={(e) =>
                          handleFundraiserCauseChange(e.target.value, field.onChange)
                        }
                      />
                    </div>
                    <span className="store-details-form__char-count">
                      {(field.value ?? '').length}/{STORE_INFO_CHAR_LIMITS.CHARITY_NAME}
                    </span>
                  </>
                )}
              />
            </div>
            <div className="store-details-form__field">
              <div className="store-details-form__label">
                How much do you want to raise? (Optional)
              </div>
              <Controller
                name="raiseAmount"
                control={control}
                render={({ field }) => (
                  <div className="store-details-form__input store-details-form__input--currency-prefix">
                    <Input
                      {...field}
                      name="fundraiser-goal"
                      label="e.g. $500"
                      floatingLabel={false}
                      type="text"
                      contentBefore="$"
                      disabled={isReadOnly}
                      onChange={(e) => handleFundraiserGoalChange(e.target.value, field.onChange)}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        )}

        <div className="store-details-form__row store-details-form__row--customization">
          <div className="store-details-form__field store-details-form__field--full-width">
            <div className="store-details-branding stores-design-your-store--embedded stores-design-your-store--embedded-in-store-details">
              <div className="store-details-form__label">Store Customization</div>
              <div className="store-details-branding__logo-and-colors">
                <div className="store-details-branding__logo-block">
                  <div
                    className={`store-details-branding__logo-upload ${
                      branding.logoUploadErrorMessage
                        ? 'store-details-branding__logo-upload--error'
                        : ''
                    } ${
                      branding.isLogoUploading ? 'store-details-branding__logo-upload--loading' : ''
                    }`}
                    aria-busy={branding.isLogoUploading}
                  >
                    {branding.showStoreLogoRemoveButton && !branding.isLogoUploading && (
                      <button
                        type="button"
                        className="store-details-branding__logo-remove"
                        onClick={branding.handleRemoveLogo}
                        disabled={isReadOnly}
                        aria-label="Remove uploaded store logo"
                      >
                        <svg
                          className="store-details-branding__logo-remove-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 10 10"
                          width="10"
                          height="10"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M2.5 2.5 7.5 7.5M7.5 2.5 2.5 7.5"
                            stroke="currentColor"
                            strokeWidth="1.25"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                    <div className="store-details-branding__logo-preview store-details-branding__logo-preview--has-image">
                      {storeLogoPreviewSrc ? (
                        <img
                          src={storeLogoPreviewSrc}
                          alt="Store logo preview"
                          className="store-details-branding__logo-img"
                        />
                      ) : (
                        <div className="store-details-branding__logo-placeholder">ΔΔΔ</div>
                      )}
                    </div>
                    {branding.isLogoUploading && (
                      <div
                        className="store-details-branding__logo-upload-loader"
                        role="status"
                        aria-live="polite"
                        data-testid="store-logo-upload-loader"
                      >
                        <span className="store-details-branding__logo-upload-loader-label">
                          {STORE_LOGO_UPLOAD_MESSAGES.UPLOADING_LABEL}
                        </span>
                        <div
                          className="store-details-branding__logo-upload-progress"
                          aria-hidden="true"
                        >
                          <div
                            className="store-details-branding__logo-upload-progress-bar"
                            style={{ width: `${branding.logoUploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <input
                      ref={branding.fileInputRef}
                      type="file"
                      accept={STORE_LOGO_UPLOAD.ACCEPT_ATTR}
                      className="store-details-branding__logo-file-input"
                      onChange={branding.handleLogoUpload}
                      aria-invalid={!!branding.logoUploadErrorMessage}
                      disabled={branding.isLogoUploading}
                    />
                  </div>
                  <div className="store-details-branding__logo-upload-actions">
                    <div className="store-details-branding__logo-upload-btn">
                      <button
                        type="button"
                        onClick={branding.handleUploadClick}
                        disabled={isReadOnly || branding.isLogoUploading}
                        className="store-details-branding__upload-btn"
                      >
                        <svg
                          className="store-details-branding__upload-btn-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload a Logo
                      </button>
                    </div>
                    {!branding.logoUploadErrorMessage ? (
                      <p className="store-details-branding__logo-upload-helper">
                        {STORE_LOGO_UPLOAD_MESSAGES.HORIZONTAL_LOGO_HELPER}
                      </p>
                    ) : null}
                    {branding.logoUploadErrorMessage ? (
                      <p
                        className="store-details-branding__logo-upload-error"
                        role="alert"
                        data-testid="stores-checkout-design-your-store-logo-upload-error"
                      >
                        {branding.logoUploadErrorMessage}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="store-details-branding__color-block">
                  {branding.extractedColors.length > 0 && (
                    <div className="store-details-branding__color-group">
                      <div className="store-details-branding__field-label">
                        Pick your store&apos;s primary color from your logo
                      </div>
                      <div className="store-details-branding__swatches">
                        {branding.extractedColors.map((color) => {
                          const isLight =
                            color.toLowerCase() === '#ffd93d' || color.toLowerCase() === '#f39c12';
                          return (
                            <button
                              key={color}
                              type="button"
                              className={`store-details-branding__swatch ${
                                isLight ? 'store-details-branding__swatch--light' : ''
                              } ${
                                branding.selectedColor === color
                                  ? 'store-details-branding__swatch--selected'
                                  : ''
                              }`}
                              style={{ backgroundColor: color }}
                              onClick={() =>
                                branding.selectColor(color, STORE_PRIMARY_COLOR_SOURCE.EXTRACTED)
                              }
                              disabled={isReadOnly}
                              aria-label={`Select color ${color}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="store-details-branding__color-group">
                    <div className="store-details-branding__field-label">
                      {branding.extractedColors.length > 0
                        ? "Or pick your store's primary color from here"
                        : "Pick your store's primary color"}
                    </div>
                    <div className="store-details-branding__swatches">
                      {allPresetSwatches.map((color) => {
                        const isLight =
                          color.toLowerCase() === '#ffd93d' || color.toLowerCase() === '#f39c12';
                        return (
                          <button
                            key={color}
                            type="button"
                            className={`store-details-branding__swatch ${
                              isLight ? 'store-details-branding__swatch--light' : ''
                            } ${
                              branding.selectedColor === color
                                ? 'store-details-branding__swatch--selected'
                                : ''
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() =>
                              branding.selectColor(color, STORE_PRIMARY_COLOR_SOURCE.PRESET)
                            }
                            disabled={isReadOnly}
                            aria-label={`Select color ${color}`}
                          />
                        );
                      })}
                      {branding.customColors.map((color) => (
                        <div key={color} className="store-details-branding__swatch-wrap">
                          <button
                            type="button"
                            className={`store-details-branding__swatch ${
                              branding.selectedColor === color
                                ? 'store-details-branding__swatch--selected'
                                : ''
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() =>
                              branding.selectColor(color, STORE_PRIMARY_COLOR_SOURCE.CUSTOM)
                            }
                            disabled={isReadOnly}
                            aria-label={`Select color ${color}`}
                          />
                          {!isReadOnly ? (
                            <button
                              type="button"
                              className="store-details-branding__swatch-remove"
                              aria-label="Remove custom color"
                              data-testid="stores-checkout-design-your-store-remove-custom-color"
                              onClick={(event) => {
                                event.stopPropagation();
                                branding.removeCustomColor(color);
                              }}
                            >
                              <svg
                                className="store-details-branding__swatch-remove-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 10 10"
                                fill="none"
                                aria-hidden="true"
                              >
                                <path
                                  d="M2.5 2.5 7.5 7.5M7.5 2.5 2.5 7.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          ) : null}
                        </div>
                      ))}
                      <div className="store-details-branding__swatch-add-slot">
                        {!branding.showColorPicker ? (
                          <button
                            type="button"
                            className="store-details-branding__swatch-add"
                            onClick={branding.openColorPicker}
                            disabled={isReadOnly}
                            aria-label={
                              branding.customColors.length > 0
                                ? 'Edit custom color'
                                : 'Add custom color'
                            }
                            data-testid="stores-checkout-design-your-store-add-custom-color"
                          >
                            {branding.customColors.length > 0 ? (
                              <svg
                                className="store-details-branding__swatch-add-edit"
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 9.3 9.3"
                                aria-hidden="true"
                              >
                                <path
                                  d="M13.948,2.231a1.2,1.2,0,1,1,1.7,1.7L10.266,9.312,8,9.879l.567-2.266Z"
                                  transform="translate(-7.35 -1.229)"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="1.3"
                                />
                              </svg>
                            ) : (
                              <span
                                className="store-details-branding__swatch-add-plus"
                                aria-hidden="true"
                              >
                                +
                              </span>
                            )}
                          </button>
                        ) : (
                          <StoreCheckoutColorPicker
                            disabled={isReadOnly}
                            initialHex={branding.colorPickerInitialHex}
                            existingColors={colorPickerExistingColors}
                            onAddColor={branding.addCustomColorFromPicker}
                            onClose={branding.closeColorPicker}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="store-details-form__row store-details-form__row--toggle">
          <div className="store-details-form__field store-details-form__field--full-width">
            <Controller
              name="displayMadeWith"
              control={control}
              render={({ field }) => (
                <label className="store-details-form__toggle">
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => handleDisplayMadeWithChange(e.target.checked, field.onChange)}
                    disabled={isReadOnly}
                    className="store-details-form__toggle-input"
                  />
                  <span className="store-details-form__toggle-slider" />
                  <span className="store-details-form__toggle-label">{madeWithLoveLabel}</span>
                </label>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

StoreDetails.displayName = 'StoreDetails';

export default StoreDetails;
